import { useEffect, useState } from "react";
import { listMyLicenses, type LicenseDoc, formatExpiry } from "../api/functions";

export function MyLicensesPage() {
  const [licenses, setLicenses] = useState<LicenseDoc[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    listMyLicenses()
      .then(r => setLicenses(r.licenses))
      .catch(e => setError(e?.message ?? "Failed to load licenses"));
  }, []);

  const copy = (key: string) => {
    navigator.clipboard.writeText(key);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-slate-800">My licenses</h1>
      <p className="text-slate-500 text-sm">
        Use these keys to activate DinFlow on your restaurant PC. Click a key to copy it.
      </p>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {licenses === null && !error && <div className="text-slate-500">Loading…</div>}

      {licenses && licenses.length === 0 && (
        <div className="text-slate-500 text-sm">
          No licenses yet. Contact support if you expected one.
        </div>
      )}

      {licenses && licenses.length > 0 && (
        <div className="space-y-3">
          {licenses.map(l => (
            <div key={l.docId} className="bg-white rounded-lg shadow p-5">
              <div className="flex items-baseline justify-between">
                <div className="font-semibold text-slate-800">{l.restaurantName}</div>
                <Status status={l.status} />
              </div>

              <button
                onClick={() => copy(l.licenseKey)}
                className="mt-3 block w-full text-left bg-slate-50 hover:bg-slate-100 rounded-md px-4 py-3 font-mono text-sm tracking-wider text-slate-800 border border-slate-200"
                title="Click to copy"
              >
                {l.licenseKey}
                <span className="float-right text-xs text-indigo-600">
                  {copied === l.licenseKey ? "Copied!" : "Copy"}
                </span>
              </button>

              <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <dt className="text-slate-500">Restaurant ID</dt>
                <dd className="text-slate-800 font-mono">{l.restaurantId || "—"}</dd>
                <dt className="text-slate-500">Expiry</dt>
                <dd className="text-slate-800">{formatExpiry(l.expiry)}</dd>
                <dt className="text-slate-500">Activated on</dt>
                <dd className="text-slate-800">
                  {l.machineId ? (l.machineName ?? l.machineId) : "Not yet activated"}
                </dd>
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Status({ status }: { status: string }) {
  const styles =
    status === "active"    ? "bg-emerald-100 text-emerald-800" :
    status === "suspended" ? "bg-amber-100 text-amber-800" :
                             "bg-slate-200 text-slate-700";
  return (
    <span className={"inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium " + styles}>
      {status}
    </span>
  );
}
